/**
 * 仪表盘（Alice 页面 overview「运营驾驶舱」的复刻）：周期内的活跃、请求、消费、商业盈亏、充值、Token 与留存。
 *
 * 背景：运营每天先看这一页：卖了多少、上游花了多少、净赚多少、谁在用。原先是 @ap/ui 风格的简版（按模型、
 *   渠道分组的表），现换成 Alice 的版式；数据来自 /control/admin/stats 与 /stats/trend（收入由控制面向支付服务聚合）。
 * 设计意图：
 *   - 版式、控件、图表与 Alice 逐块对应：排除内部开关（记在浏览器里）、1/3/7/14/30 天与自选日期（最长 90 天）、
 *     四张 KPI、盈亏与充值与 Token 三张业务卡、八张图、两个排行、留存明细。
 *   - 金额美元，花瓣只在用户消费处并列；用户换成账号；「内部」换成「仅记账」请求。
 *   - 仅记账请求不进营收与成本，页面在盈亏卡下写明条数与面值；成本未知的商业请求同样单列，不当作零成本。
 *   - 「生活线按场景」依赖客户端上报的细分来源，本仓没有，图保留并显示空状态。
 *   - 数据面配置版本与实例状态（Asterove 才有的运维信息）保留在页面底部。
 * 关键约束：收入类数据取自支付服务，取不到时后端给 payments_error，页面提示，不当作没有收入；
 *   加载以查询键为准，快速切换周期时旧请求不会覆盖新结果（useLoad 内部按依赖丢弃过期结果）。
 */
import { IconActivity, IconBox, IconCheck, IconCoin, IconUsers } from '@tabler/icons-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { adminApi } from '@ap/api-client/admin'
import { bizApi, type OverviewStats, type OverviewTrend } from '@ap/api-client/admin-biz'
import { UNITS_PER_USD, fmtInt, fmtPetals } from '@ap/api-client/money'
import type { ConfigStatus } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { BIZ } from '@ap/i18n/admin-biz'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import { Badge, Button, Card, Empty, Link, Loading, Notice, Table, fmtDateTime, useLoad, useToast, type Column } from '@ap/ui'
import {
  ChartCanvas,
  FIELD,
  beijingDate,
  bizError,
  chartOpts,
  compactTokens,
  ratePct,
  signColor,
  spanDays,
  stackDatasets,
} from '../alice/biz-kit.tsx'
import { useUsd } from '../shared.tsx'

const PRESETS = [1, 3, 7, 14, 30] as const
const LS_EXCL = 'asterove_admin_exclude_internal'
const LS_COMPACT = 'asterove_admin_token_compact'
const RETENTION = [
  { key: 'd1', label: BIZ.ov.ret1, color: '#8b5cf6' },
  { key: 'd3', label: BIZ.ov.ret3, color: '#3b82f6' },
  { key: 'd7', label: BIZ.ov.ret7, color: '#10b981' },
  { key: 'd14', label: BIZ.ov.ret14, color: '#f59e0b' },
  { key: 'd30', label: BIZ.ov.ret30, color: '#ef4444' },
  { key: 'd60', label: BIZ.ov.ret60, color: '#6b7280' },
] as const

const readFlag = (key: string, fallback: boolean) => {
  try {
    const v = localStorage.getItem(key)
    return v === null ? fallback : v === '1'
  } catch (e) {
    console.error('[dashboard] 读取本地设置失败', key, e)
    return fallback
  }
}
const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, value ? '1' : '0')
  } catch (e) {
    console.error('[dashboard] 写入本地设置失败', key, e)
  }
}

export function Dashboard() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const toast = useToast()
  const [start, setStart] = useState(beijingDate(0))
  const [end, setEnd] = useState(beijingDate(0))
  const [excl, setExcl] = useState(() => readFlag(LS_EXCL, true))
  const [compact, setCompact] = useState(() => readFlag(LS_COMPACT, false))
  const config = useLoad(() => adminApi.config(), [])

  const stats = useLoad<OverviewStats>(() => bizApi.stats({ start_date: start, end_date: end, exclude_internal: excl }), [start, end, excl])
  const trend = useLoad<OverviewTrend>(() => bizApi.trend({ start_date: start, end_date: end, exclude_internal: excl }), [start, end, excl])
  const s = stats.data
  const tr = trend.data

  useEffect(() => {
    if (stats.error) toast.error(bizError(stats.error, locale))
  }, [stats.error, toast, locale])

  const today = beijingDate(0)
  const days = spanDays(start, end)
  const setDays = (n: number) => {
    setStart(beijingDate(-(n - 1)))
    setEnd(today)
  }
  const setRange = (a: string, b: string) => {
    if (!a || !b) return
    if (a > b) return toast.error(t(BIZ.ov.badRange))
    if (spanDays(a, b) > 90) return toast.error(t(BIZ.ov.rangeTooLong))
    setStart(a)
    setEnd(b)
  }
  const toggleExcl = () => {
    setExcl((v) => {
      writeFlag(LS_EXCL, !v)
      return !v
    })
  }
  const setTokenMode = (c: boolean) => {
    setCompact(c)
    writeFlag(LS_COMPACT, c)
  }

  const label = (() => {
    if (end === today && start === today) return t(BIZ.ov.periodToday)
    if (end === today && PRESETS.some((n) => n === days && start === beijingDate(-(n - 1)))) return t(BIZ.ov.periodDays, { n: days })
    if (start === end) return start.slice(5)
    return `${start.slice(5)} ${t(BIZ.common.to)} ${end.slice(5)}`
  })()

  const int = (n: number) => fmtInt(n, tag)
  const tok = (n: number) => (compact ? compactTokens(n) : fmtInt(Math.round(n), 'en-US'))
  const pts = (u: number) => fmtPetals(u, tag)

  // ------------------------------------------------------------------ 图表
  const charts = useMemo(() => {
    if (!tr) return null
    const labels = tr.daily.map((r) => r.date.slice(5))
    const dates = tr.daily.map((r) => r.date)
    const toUsd = (u: number) => u / UNITS_PER_USD
    const lineName = (k: string) => (k ? k : t(BIZ.ov.unclassified))
    const otherLabel = (n: number) => t(BIZ.ov.otherItems, { n })
    const stackTip = (footer: (items: { parsed: { y: number }; dataIndex: number }[]) => string | string[]) =>
      ({
      mode: 'index',
      itemSort: (a: { parsed: { y: number } }, b: { parsed: { y: number } }) => b.parsed.y - a.parsed.y,
      filter: (item: { parsed: { y: number } }) => item.parsed.y > 0,
      callbacks: { label: (item: { dataset: { label: string }; parsed: { y: number } }) => `${item.dataset.label}: $${item.parsed.y.toFixed(2)}`, footer },
      }) as never
    const totalFooter = (items: { parsed: { y: number } }[]) => t(BIZ.ov.tipTotal, { v: items.reduce((a, it) => a + it.parsed.y, 0).toFixed(2) })

    const pnlOpts = chartOpts()
    pnlOpts.scales.x.stacked = true
    pnlOpts.scales.y.stacked = true
    pnlOpts.plugins.tooltip = stackTip((items) => {
      const i = items[0].dataIndex
      const row = tr.daily[i]
      const internal = toUsd(row.internal_cost_units)
      const cost = toUsd(row.commercial_cost_units) + internal
      const net = toUsd(row.revenue_units - row.commercial_cost_units)
      return [t(BIZ.ov.tipCost, { c: cost.toFixed(2), i: internal.toFixed(2) }), t(BIZ.ov.tipNet, { v: `${net >= 0 ? '+' : '−'}$${Math.abs(net).toFixed(2)}` })]
    })
    const modelOpts = chartOpts()
    modelOpts.scales.x.stacked = true
    modelOpts.scales.y.stacked = true
    modelOpts.plugins.tooltip = stackTip(totalFooter)

    const dau = tr.dau_daily
    const dauLabels = dau.map((r) => r.date.slice(5))
    const retOpts = chartOpts()
    retOpts.scales = {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, max: 100, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 }, callback: (v: unknown) => `${v}%` } },
    }
    retOpts.plugins = {
      legend: { position: 'top', align: 'end', labels: { font: { size: 9 }, usePointStyle: true, pointStyle: 'circle', boxWidth: 6 } },
      title: { display: false },
      tooltip: { callbacks: { label: ((ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => `${ctx.dataset.label ?? ''}: ${ctx.parsed.y !== null ? `${ctx.parsed.y}%` : t(BIZ.ov.noData)}`) as never } },
    }
    const dauOpts = chartOpts()
    dauOpts.scales = {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 } } },
    }

    const line = (label: string, data: (number | null)[], color: string, fill = true, w = 2) => ({
      label,
      data,
      borderColor: color,
      backgroundColor: fill ? `${color}14` : 'transparent',
      fill,
      tension: 0.3,
      pointRadius: 2,
      borderWidth: w,
    })

    return {
      pnl: {
        type: 'bar' as const,
        data: {
          labels,
          datasets: [
            { label: t(BIZ.ov.chartRevenue), data: tr.daily.map((r) => toUsd(r.revenue_units)), backgroundColor: 'rgba(16,185,129,0.7)', borderRadius: 4, stack: 'revenue' },
            ...stackDatasets(
              tr.line_summary.map((r) => ({ key: r.line ?? '' })),
              tr.line_daily,
              dates,
              { nameOf: lineName, otherLabel, valueOf: toUsd },
            ),
          ],
        },
        options: pnlOpts,
      },
      model: {
        type: 'bar' as const,
        data: {
          labels,
          datasets: stackDatasets(
            tr.model_summary.map((r) => ({ key: r.model ?? '' })),
            tr.model_daily,
            dates,
            { otherLabel, valueOf: toUsd },
          ),
        },
        options: modelOpts,
      },
      cost: { type: 'line' as const, data: { labels, datasets: [{ ...line(t(BIZ.ov.chartCharge), tr.daily.map((r) => toUsd(r.charge_units)), '#dc2626'), pointRadius: 3 }] }, options: chartOpts() },
      requests: {
        type: 'bar' as const,
        data: { labels, datasets: [{ label: t(BIZ.ov.chartRequests), data: tr.daily.map((r) => r.requests), backgroundColor: 'rgba(139,92,246,0.6)', borderRadius: 4 }] },
        options: chartOpts(),
      },
      tokens: {
        type: 'line' as const,
        data: {
          labels,
          datasets: [
            line(t(BIZ.ov.tokIn), tr.daily.map((r) => r.input_tokens), '#3b82f6'),
            line(t(BIZ.ov.tokOut), tr.daily.map((r) => r.output_tokens), '#10b981'),
            line(t(BIZ.ov.tokCached), tr.daily.map((r) => r.cached_tokens), '#f59e0b', true, 1.5),
          ],
        },
        options: chartOpts(),
      },
      dau: {
        type: 'bar' as const,
        data: {
          labels: dauLabels,
          datasets: [
            { label: 'DAU', data: dau.map((r) => r.dau), backgroundColor: 'rgba(59,130,246,0.6)', borderRadius: 4 },
            { label: t(BIZ.ov.chartNew), data: dau.map((r) => r.new_accounts), backgroundColor: 'rgba(16,185,129,0.6)', borderRadius: 4 },
          ],
        },
        options: dauOpts,
      },
      retention: {
        type: 'line' as const,
        data: {
          labels: dauLabels,
          datasets: RETENTION.map((c) => ({ ...line(t(c.label), dau.map((r) => r.retention[c.key as 'd1']), c.color, false), spanGaps: true })),
        },
        options: retOpts,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tr, locale])

  const pnl = s?.pnl
  const tk = s?.tokens
  const cacheRate = tk && tk.input > 0 ? Math.min(100, (tk.cached * 100) / tk.input) : 0
  const m = s?.metrics

  const retCell = (v: number | null) => {
    if (v === null || v === undefined) return <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--text-tertiary)' }}>—</td>
    const style =
      v >= 80 ? { background: 'rgba(16,185,129,0.12)', color: '#059669' } : v >= 50 ? { background: 'rgba(59,130,246,0.1)', color: '#2563eb' } : v >= 30 ? { background: 'rgba(245,158,11,0.1)', color: '#d97706' } : { background: 'rgba(239,68,68,0.1)', color: '#dc2626' }
    return <td style={{ textAlign: 'right', padding: '4px 8px', borderRadius: 4, fontWeight: 500, ...style }}>{v}%</td>
  }

  const updated = s ? t(BIZ.ov.updated, { p: label, s: t(excl ? BIZ.ov.scopeExcl : BIZ.ov.scopeIncl) }) : stats.error ? t(BIZ.ca.failed) : t(BIZ.ov.waiting)

  return (
    <div id="panel-overview">
      <div className="overview-head">
        <div>
          <div className="overview-eyebrow">BUSINESS PULSE</div>
          <h1>{t(BIZ.ov.title)}</h1>
          <p>{t(BIZ.ov.desc)}</p>
        </div>
        <div className="overview-head-actions">
          <span>{updated}</span>
          <div className="overview-control-row">
            <button type="button" className={`overview-scope-toggle${excl ? ' active' : ''}`} onClick={toggleExcl} aria-pressed={excl} title={t(BIZ.ov.scopeTitle)}>
              <span className="scope-check">{excl && <IconCheck size={10} stroke={3} />}</span>
              <span>{t(BIZ.ov.excludeInternal)}</span>
            </button>
            <div className="period-switch">
              {PRESETS.map((n) => (
                <button key={n} type="button" className={end === today && start === beijingDate(-(n - 1)) ? 'active' : ''} onClick={() => setDays(n)}>
                  {t(BIZ.ov.presetDays, { n })}
                </button>
              ))}
            </div>
            <div className="overview-date-range">
              <input type="date" style={FIELD} value={start} max={today} onChange={(e) => setRange(e.target.value, end)} aria-label={t(BIZ.mu.start)} />
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(BIZ.common.to)}</span>
              <input type="date" style={FIELD} value={end} max={today} onChange={(e) => setRange(start, e.target.value)} aria-label={t(BIZ.mu.end)} />
            </div>
          </div>
        </div>
      </div>

      {s?.payments_error && <Notice tone="danger">{t(BIZ.common.paymentsDown)}</Notice>}
      {!!stats.error && !s && <Notice tone="danger">{errorText(stats.error, locale)}</Notice>}

      <div className="overview-kpi-grid">
        <div className="overview-kpi-card blue">
          <div className="kpi-icon">
            <IconUsers size={16} />
          </div>
          <div className="stat-label">{t(BIZ.ov.kActive, { p: label })}</div>
          <div className="stat-value">
            <span>{s ? int(s.active_accounts) : '—'}</span>
            <small> {t(BIZ.ov.orgUnit)}</small>
          </div>
          <div className="stat-sub">
            {t(BIZ.ov.kTotalOrgs)} <strong>{s ? int(s.total_accounts) : '—'}</strong>
          </div>
        </div>
        <div className="overview-kpi-card violet">
          <div className="kpi-icon">
            <IconActivity size={16} />
          </div>
          <div className="stat-label">{t(BIZ.ov.kRequests, { p: label })}</div>
          <div className="stat-value">{s ? int(s.requests) : '—'}</div>
          <div className="stat-sub">
            {t(BIZ.ov.kPerOrg)} <strong>{s ? s.metrics.requests_per_account.toFixed(1) : '—'}</strong> {t(BIZ.ov.timesUnit)}
          </div>
        </div>
        <div className="overview-kpi-card emerald">
          <div className="kpi-icon">
            <IconCoin size={16} />
          </div>
          <div className="stat-label">{t(BIZ.ov.kCharge, { p: label })}</div>
          <div className="stat-value">
            {s ? usd(s.charge_units) : '—'}
            {s && <small> {t(BIZ.ov.petalsUnit, { n: pts(s.charge_units) })}</small>}
          </div>
          <div className="stat-sub">
            {t(BIZ.ov.kTotalCharge)} <strong>{s ? usd(s.total_charge_units) : '—'}</strong>
          </div>
        </div>
        <div className="overview-kpi-card amber">
          <div className="kpi-icon">
            <IconBox size={16} />
          </div>
          <div className="stat-label">{t(BIZ.ov.kModels)}</div>
          <div className="stat-value">{s ? int(s.total_models) : '—'}</div>
          <div className="stat-sub">{t(BIZ.ov.kModelsSub, { n: s ? int(s.active_keys) : '—' })}</div>
        </div>
      </div>

      <div className="business-grid">
        <div className="business-card profit-card">
          <div className="business-card-head">
            <span>{t(BIZ.ov.pnlTitle, { p: label })}</span>
            <span className="live-pill">{t(BIZ.ov.pnlPill)}</span>
          </div>
          <div className="profit-value" style={pnl ? { color: signColor(pnl.net_units) } : undefined}>
            {pnl ? `${pnl.net_units >= 0 ? '+' : '−'}${usd(Math.abs(pnl.net_units))}` : '—'}
          </div>
          <div className="profit-copy">{t(BIZ.ov.pnlCopy)}</div>
          <div className="metric-strip dark">
            <div className="metric-item">
              <div className="m-label">{t(BIZ.ov.pnlRevenue)}</div>
              <div className="m-value">{pnl ? usd(pnl.revenue_units) : '—'}</div>
            </div>
            <div className="metric-item">
              <div className="m-label">{t(BIZ.ov.pnlCost)}</div>
              <div className="m-value">{pnl ? usd(pnl.commercial_cost_units) : '—'}</div>
            </div>
            <div className="metric-item">
              <div className="m-label">{t(BIZ.ov.pnlInternal)}</div>
              <div className="m-value">{pnl ? usd(pnl.internal_cost_units) : '—'}</div>
            </div>
          </div>
          {pnl && (pnl.metered_requests > 0 || pnl.commercial_unknown_requests > 0) && (
            <div className="profit-copy" style={{ marginTop: 10 }}>
              {[
                pnl.metered_requests > 0 ? t(BIZ.ov.pnlMetered, { n: int(pnl.metered_requests), v: usd(pnl.metered_list_units) }) : '',
                pnl.commercial_unknown_requests > 0 ? t(BIZ.ov.pnlUnknown, { n: int(pnl.commercial_unknown_requests) }) : '',
              ]
                .filter(Boolean)
                .join(' ')}
            </div>
          )}
        </div>
        <div className="business-card">
          <div className="business-card-head">
            <span>{t(BIZ.ov.rechargeTitle, { p: label })}</span>
            <span className="card-dot success" />
          </div>
          <div className="business-value">{s ? usd(s.recharge.amount_units) : '—'}</div>
          <div className="stat-sub">{s ? t(BIZ.ov.rechargeSub, { o: int(s.recharge.order_count), p: int(s.recharge.paying_accounts) }) : '—'}</div>
          <div className="metric-strip">
            <div className="metric-item">
              <div className="m-label">ARPU</div>
              <div className="m-value">{m ? usd(m.arpu_units) : '—'}</div>
            </div>
            <div className="metric-item">
              <div className="m-label">ARPPU</div>
              <div className="m-value">{m ? usd(m.arppu_units) : '—'}</div>
            </div>
            <div className="metric-item">
              <div className="m-label">{t(BIZ.ov.payRate)}</div>
              <div className="m-value">{m ? ratePct(m.paying_rate) : '—'}</div>
            </div>
          </div>
        </div>
        <div className="business-card">
          <div className="business-card-head">
            <span>{t(BIZ.ov.tokensTitle, { p: label })}</span>
            <div className="token-mode-switch">
              <button type="button" className={compact ? '' : 'active'} onClick={() => setTokenMode(false)}>
                {t(BIZ.ov.tokFull)}
              </button>
              <button type="button" className={compact ? 'active' : ''} onClick={() => setTokenMode(true)}>
                {t(BIZ.ov.tokCompact)}
              </button>
            </div>
          </div>
          <div className="business-value">{tk ? tok(tk.input + tk.output) : '—'}</div>
          <div className="stat-sub">
            {t(BIZ.ov.tokPerOrg)} <strong>{m ? tok(m.tokens_per_account) : '—'}</strong> · {t(BIZ.ov.chargePerOrg)} <strong>{m ? usd(m.charge_per_account_units) : '—'}</strong>
          </div>
          <div className="cache-progress">
            <div style={{ width: `${cacheRate}%` }} />
          </div>
          <div className="cache-copy">
            <span>
              {t(BIZ.ov.cacheRate)} <strong>{cacheRate.toFixed(1)}%</strong>
            </span>
            <span>
              {t(BIZ.ov.cacheHit)} <strong>{tk ? tok(tk.cached) : '—'}</strong>
            </span>
          </div>
          <div className="token-flow">
            <span>
              {t(BIZ.ov.tokIn)} <strong>{tk ? tok(tk.input) : '—'}</strong>
            </span>
            <span>
              {t(BIZ.ov.tokOut)} <strong>{tk ? tok(tk.output) : '—'}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="overview-section-head">
        <div>
          <h2>{t(BIZ.ov.trendTitle)}</h2>
          <p>{t(BIZ.ov.trendCopy)}</p>
        </div>
      </div>
      <div className="dashboard-chart-grid">
        <div className="dashboard-chart-card chart-wide">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cPnl)}</span>
            <small>{t(BIZ.ov.usd)}</small>
          </div>
          <div className="chart-h-260">{charts && <ChartCanvas config={charts.pnl} label={t(BIZ.ov.cPnl)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cCharge)}</span>
            <small>{t(BIZ.ov.usd)}</small>
          </div>
          <div className="chart-h-210">{charts && <ChartCanvas config={charts.cost} label={t(BIZ.ov.cCharge)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cRequests)}</span>
            <small>{t(BIZ.ov.cRequestsSub)}</small>
          </div>
          <div className="chart-h-210">{charts && <ChartCanvas config={charts.requests} label={t(BIZ.ov.cRequests)} />}</div>
        </div>
        <div className="dashboard-chart-card chart-wide">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cTokens)}</span>
            <small>{t(BIZ.ov.cTokensSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.tokens} label={t(BIZ.ov.cTokens)} />}</div>
        </div>
        <div className="dashboard-chart-card chart-wide">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cModel)}</span>
            <small>{t(BIZ.ov.cModelSub)}</small>
          </div>
          <div className="chart-h-260">{charts && <ChartCanvas config={charts.model} label={t(BIZ.ov.cModel)} />}</div>
        </div>
        <div className="dashboard-chart-card chart-wide">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cScene)}</span>
            <small>{t(BIZ.ov.cSceneSub)}</small>
          </div>
          <div className="empty">{t(BIZ.ov.sceneEmpty)}</div>
        </div>
      </div>

      <div className="overview-section-head">
        <div>
          <h2>{t(BIZ.ov.growthTitle)}</h2>
          <p>{t(BIZ.ov.growthCopy)}</p>
        </div>
      </div>
      <div className="dashboard-chart-grid">
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cDau)}</span>
            <small>DAU</small>
          </div>
          <div className="chart-h-240">{charts && <ChartCanvas config={charts.dau} label={t(BIZ.ov.cDau)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.cRetention)}</span>
            <small>%</small>
          </div>
          <div className="chart-h-240">{charts && <ChartCanvas config={charts.retention} label={t(BIZ.ov.cRetention)} />}</div>
        </div>
      </div>

      <div className="ranking-grid">
        <div className="ranking-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.topOrgs)}</span>
            <small>{t(BIZ.ov.topOrgsSub)}</small>
          </div>
          <div>
            {!tr || tr.top_accounts.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>{tr ? t(BIZ.common.noData) : t(BIZ.common.loading)}</div>
            ) : (
              tr.top_accounts.slice(0, 8).map((o, i) => (
                <div key={o.account_id} className="rank-row">
                  <div className="rank-number">{i + 1}</div>
                  <div>
                    <div className="rank-name">
                      <Link href={`/accounts/${o.account_id}`}>{o.name}</Link>
                    </div>
                    <div className="rank-meta">{t(BIZ.ov.reqCount, { n: int(o.requests) })}</div>
                  </div>
                  <div className="rank-value">{usd(o.charge_units)}</div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="ranking-card">
          <div className="chart-card-head">
            <span>{t(BIZ.ov.topModels)}</span>
            <small>{t(BIZ.ov.topModelsSub)}</small>
          </div>
          <div>
            {!tr || tr.top_models.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>{tr ? t(BIZ.common.noData) : t(BIZ.common.loading)}</div>
            ) : (
              tr.top_models.slice(0, 10).map((x, i) => (
                <div key={x.model} className="rank-row">
                  <div className="rank-number">{i + 1}</div>
                  <div>
                    <div className="rank-name mono">{x.model}</div>
                    <div className="rank-meta">{t(BIZ.ov.modelMeta, { t: compactTokens(x.tokens), r: int(x.requests), u: usd(x.upstream_units, 4) })}</div>
                  </div>
                  <div className="rank-value">{usd(x.charge_units)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <details className="retention-detail">
        <summary>
          <span>{t(BIZ.ov.retDetail)}</span>
          <small>{t(BIZ.ov.retDetailHint)}</small>
        </summary>
        <div className="retention-table-wrap">
          <table id="ov-retention-table">
            <thead>
              <tr>
                <th>{t(BIZ.ov.colDate)}</th>
                <th>DAU</th>
                <th>{t(BIZ.ov.chartNew)}</th>
                {RETENTION.map((c) => (
                  <th key={c.key}>{t(c.label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!tr || tr.dau_daily.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 16 }}>
                    {tr ? t(BIZ.common.noData) : t(BIZ.common.loading)}
                  </td>
                </tr>
              ) : (
                tr.dau_daily
                  .slice()
                  .reverse()
                  .map((r) => (
                    <tr key={r.date} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{r.date.slice(5)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600 }}>{r.dau}</td>
                      <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--success-text)' }}>{r.new_accounts || 0}</td>
                      {RETENTION.map((c) => (
                        <Fragment key={c.key}>{retCell(r.retention[c.key as 'd1'])}</Fragment>
                      ))}
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </details>

      <div style={{ marginTop: 24 }}>
        <ConfigCard config={config.data} error={config.error} loading={config.loading} />
      </div>
    </div>
  )
}

function ConfigCard({ config, error, loading }: { config: ConfigStatus | null; error: unknown; loading: boolean }) {
  const { t, locale } = useI18n()
  const [showStale, setShowStale] = useState(false)
  type Inst = ConfigStatus['instances'][number]
  type Change = ConfigStatus['recent'][number]
  const instCols: Column<Inst>[] = [
    { key: 'i', title: t(A.dash.instance), render: (x) => <span className="ap-mono">{x.instance}</span> },
    { key: 'v', title: t(A.dash.version), num: true, render: (x) => x.version ?? '-' },
    {
      key: 's',
      title: t(C.status),
      render: (x) => (
        <div className="ap-row" style={{ gap: 6 }}>
          <Badge tone={x.alive ? 'success' : 'danger'}>{t(x.alive ? A.dash.alive : A.dash.stale)}</Badge>
          <Badge tone={x.up_to_date ? 'success' : 'warning'}>{t(x.up_to_date ? A.dash.upToDate : A.dash.behind)}</Badge>
        </div>
      ),
    },
    {
      key: 'seen',
      title: '',
      muted: true,
      nowrap: true,
      render: (x) =>
        x.seen_s_ago < 90
          ? t(A.dash.seen, { n: x.seen_s_ago })
          : x.seen_s_ago < 5400
            ? t(A.dash.seenMin, { n: Math.round(x.seen_s_ago / 60) })
            : t(A.dash.seenHour, { n: Math.round(x.seen_s_ago / 3600) }),
    },
  ]
  // 失联实例要保留 24 小时供排查，默认收起，免得开发机反复重启后把在线实例挤到下面。
  // 一个在线的都没有时直接全展示，那正是要排查的时候。
  const stale = config?.instances.filter((x) => !x.alive) ?? []
  const allStale = !!config && stale.length === config.instances.length
  const expanded = showStale || allStale
  const shownInstances = expanded ? config?.instances : config?.instances.filter((x) => x.alive)
  const recentCols: Column<Change>[] = [
    { key: 'v', title: t(A.dash.version), num: true, render: (x) => x.version },
    { key: 'r', title: t(C.reason), render: (x) => x.reason },
    { key: 'a', title: t(A.audit.actor), muted: true, render: (x) => <span className="ap-mono">{x.actor.slice(0, 8)}</span> },
    { key: 't', title: t(C.time), muted: true, nowrap: true, render: (x) => fmtDateTime(x.created_at, locale) },
  ]
  return (
    <Card flush title={config ? `${t(A.dash.config)} · v${config.version}` : t(A.dash.config)}>
      {error ? (
        <div className="ap-card__body">
          <Notice tone="danger">{errorText(error, locale)}</Notice>
        </div>
      ) : !config ? (
        loading ? <Loading /> : null
      ) : (
        <>
          <div className="ap-card__body">
            <strong>{t(A.dash.instances)}</strong>
          </div>
          <Table columns={instCols} rows={shownInstances} rowKey={(x) => x.instance} empty={<Empty title={t(A.dash.noInstances)} />} />
          {!expanded && stale.length > 0 && (
            <div className="ap-card__body ap-row" style={{ justifyContent: 'center' }}>
              <Button size="sm" variant="ghost" onClick={() => setShowStale(true)}>
                {t(A.dash.showStale, { n: stale.length })}
              </Button>
            </div>
          )}
          <div className="ap-card__body">
            <strong>{t(A.dash.recent)}</strong>
          </div>
          <Table columns={recentCols} rows={config.recent.slice(0, 10)} rowKey={(x) => String(x.version)} />
        </>
      )}
    </Card>
  )
}
